/**
 * Formattazione delle quantità del gestionale (magazzino, movimenti, ordini,
 * DDT, produzione, lavorazioni, report, stampe): separatore delle migliaia
 * col punto e decimali con la virgola — 5.000 g, 100.000 g, 1.000.000 g.
 * Eccezione: con unità a pezzi niente punto delle migliaia (5000 pz).
 *
 * Non si usa `toLocaleString('it-IT')` da solo: per l'italiano il raggruppamento
 * scatta solo da 10.000 in su (5000 resterebbe "5000"). Qui le migliaia si
 * inseriscono a mano, così il risultato è identico su ogni browser.
 *
 * Solo per la visualizzazione (schermo e stampe). Gli export CSV/Excel e i
 * valori degli <input type="number"> restano numeri grezzi.
 */

export interface QuantityFormatOptions {
  /** Decimali minimi (default 0). */
  minDecimals?: number;
  /** Decimali massimi (default 3: grammi, ml e kg frazionari). */
  maxDecimals?: number;
  /**
   * Unità di misura della quantità. Decide il punto delle migliaia (a pezzi
   * non si mette) e, secondo `unitDisplay`, viene mostrata accanto al numero.
   * Per un articolo usare `resolveProductUnit` (senza UDM 1 l'articolo è a pezzi).
   */
  unit?: string | null;
  /**
   * Come mostrare `unit`: 'suffix' (default) "5.000 g"; 'parens' "5.000 (g)",
   * per le colonne Quantità delle tabelle; 'none' non la mostra (la tabella ha
   * già una colonna UM) ma la usa per decidere il punto delle migliaia.
   */
  unitDisplay?: 'suffix' | 'parens' | 'none';
  /** Prefisso "+" sui positivi (i negativi hanno già il "-"). */
  signed?: boolean;
  /** Testo per null/undefined/non numerico (default ''). */
  fallback?: string;
}

// Le unità sono testo libero (ogni org le crea in `units_of_measure`, solo il
// nome): i pezzi si riconoscono dalle sigle più comuni, maiuscole/minuscole,
// con o senza punto e accento.
const PIECES_UNITS = new Set([
  'pz', 'pzz', 'pezzo', 'pezzi', 'pc', 'pcs', 'pce', 'pces', 'piece', 'pieces',
  'n', 'nr', 'nro', 'num', 'numero', 'u', 'ud', 'un', 'unit', 'units', 'unita',
]);

/** True se l'unità indica pezzi (pz, pezzi, nr, pcs, unità…). */
export function isPiecesUnit(unit: string | null | undefined): boolean {
  if (!unit) return false;
  const key = unit.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[\s.°]/g, '');
  return PIECES_UNITS.has(key);
}

export function formatQuantity(
  value: number | string | null | undefined,
  options: QuantityFormatOptions = {},
): string {
  const { minDecimals = 0, maxDecimals = 3, unit, unitDisplay = 'suffix', signed = false, fallback = '' } = options;
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;

  const max = Math.max(minDecimals, maxDecimals);
  const plain = new Intl.NumberFormat('it-IT', {
    useGrouping: false,
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: max,
  }).format(Math.abs(n));
  const [intPart, decPart] = plain.split(',');
  const grouped = isPiecesUnit(unit) ? intPart : intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const body = decPart ? `${grouped},${decPart}` : grouped;
  const isZero = /^[0,]+$/.test(plain);
  const sign = n < 0 && !isZero ? '-' : signed && n > 0 && !isZero ? '+' : '';
  const shown = unit?.trim();
  if (!shown || unitDisplay === 'none') return `${sign}${body}`;
  return unitDisplay === 'parens' ? `${sign}${body} (${shown})` : `${sign}${body} ${shown}`;
}
